(() => {
  const ROOT_SELECTOR = "[data-dusha-cart-root]";
  const REQUEST_HEADERS = {
    Accept: "application/json",
    "Content-Type": "application/json",
  };

  document.documentElement.classList.add("dusha-cart-js");

  function setHeaderCartCount(count) {
    const selectors = [
      "[data-cart-count]",
      "[data-cart-item-count]",
      ".js-cart-count",
      ".cart-count",
      ".cart-count-bubble [aria-hidden='true']",
    ];

    document.querySelectorAll(selectors.join(",")).forEach((element) => {
      if (element.closest(ROOT_SELECTOR)) return;
      element.textContent = String(count);
    });
  }

  function dispatchCartEvents(detail) {
    document.dispatchEvent(new CustomEvent("cart:updated", { detail }));
    document.dispatchEvent(new CustomEvent("cart:refresh", { detail }));
    window.dispatchEvent(new CustomEvent("dusha:cart-updated", { detail }));
  }

  function showToast(root, message) {
    const toast = root?.querySelector("[data-dusha-cart-toast]");
    if (!toast || !message) return;

    window.clearTimeout(toast.dushaTimer);
    toast.textContent = message;
    toast.classList.add("is-visible");
    toast.dushaTimer = window.setTimeout(() => {
      toast.classList.remove("is-visible");
    }, 2800);
  }

  class DushaCart {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.changeUrl = root.dataset.cartChangeUrl;
      this.addUrl = root.dataset.cartAddUrl;
      this.updateUrl = root.dataset.cartUpdateUrl;
      this.sectionsUrl = root.dataset.sectionsUrl || window.location.pathname;
      this.busy = false;
      this.noteTimer = null;
    }

    mount() {
      if (this.root.dataset.dushaCartReady === "true") return;
      this.root.dataset.dushaCartReady = "true";

      const note = this.root.querySelector("[data-dusha-cart-note]");
      if (note) note.dataset.savedValue = note.value;

      this.root.addEventListener("click", (event) => this.onClick(event));
      this.root.addEventListener("change", (event) => this.onChange(event));
      this.root.addEventListener("input", (event) => this.onInput(event));
    }

    onClick(event) {
      const quantityButton = event.target.closest("[data-dusha-quantity-button]");
      if (quantityButton && this.root.contains(quantityButton)) {
        event.preventDefault();
        if (this.busy) return;

        const line = quantityButton.closest("[data-dusha-line]");
        const input = line?.querySelector("[data-dusha-quantity-input]");
        if (!line || !input) return;

        const currentQuantity = Math.max(0, Number.parseInt(input.value, 10) || 0);
        const action = quantityButton.dataset.action;
        const nextQuantity =
          action === "increase"
            ? currentQuantity + 1
            : Math.max(0, currentQuantity - 1);

        this.changeLine(line.dataset.lineKey, nextQuantity, {
          lineKey: line.dataset.lineKey,
          action,
        });
        return;
      }

      const removeLink = event.target.closest("[data-dusha-remove]");
      if (removeLink && this.root.contains(removeLink)) {
        event.preventDefault();
        if (this.busy) return;
        this.changeLine(removeLink.dataset.lineKey, 0);
        return;
      }

      const addButton = event.target.closest("[data-dusha-add]");
      if (addButton && this.root.contains(addButton)) {
        event.preventDefault();
        if (this.busy || addButton.classList.contains("is-added")) return;
        this.addRecommendation(addButton);
      }
    }

    onChange(event) {
      const input = event.target.closest("[data-dusha-quantity-input]");
      if (!input || !this.root.contains(input) || this.busy) return;

      const line = input.closest("[data-dusha-line]");
      if (!line) return;

      const quantity = Math.max(0, Number.parseInt(input.value, 10) || 0);
      input.value = String(quantity);
      this.changeLine(line.dataset.lineKey, quantity, {
        lineKey: line.dataset.lineKey,
      });
    }

    onInput(event) {
      const note = event.target.closest("[data-dusha-cart-note]");
      if (!note || !this.root.contains(note)) return;

      const status = this.root.querySelector("[data-dusha-note-status]");
      if (status) status.textContent = "Saving…";

      window.clearTimeout(this.noteTimer);
      this.noteTimer = window.setTimeout(() => {
        this.saveNote().catch(() => {
          if (status) status.textContent = "Could not save";
        });
      }, 700);
    }

    setBusy(isBusy) {
      this.busy = isBusy;
      this.root.classList.toggle("is-loading", isBusy);
      this.root.setAttribute("aria-busy", String(isBusy));
    }

    clearError() {
      const error = this.root.querySelector("[data-dusha-cart-error]");
      if (!error) return;
      error.hidden = true;
      error.textContent = "";
    }

    showError(message) {
      const fallback = "Something went wrong. Please refresh the page and try again.";
      const errorMessage = message || fallback;
      const error = this.root.querySelector("[data-dusha-cart-error]");

      if (error) {
        error.textContent = errorMessage;
        error.hidden = false;
      }

      showToast(this.root, errorMessage);
    }

    async post(url, payload) {
      const response = await fetch(url, {
        method: "POST",
        headers: REQUEST_HEADERS,
        credentials: "same-origin",
        body: JSON.stringify(payload),
      });

      let data;
      try {
        data = await response.json();
      } catch (_error) {
        data = {};
      }

      if (!response.ok) {
        throw new Error(
          data.description ||
            data.message ||
            "The cart could not be updated. Please try again.",
        );
      }

      return data;
    }

    async saveNote({ silent = false } = {}) {
      const note = this.root.querySelector("[data-dusha-cart-note]");
      if (!note || note.value === note.dataset.savedValue) return;

      window.clearTimeout(this.noteTimer);
      const status = this.root.querySelector("[data-dusha-note-status]");

      await this.post(this.updateUrl, { note: note.value });
      note.dataset.savedValue = note.value;

      if (!silent && status) {
        status.textContent = "Saved";
        window.setTimeout(() => {
          if (status.isConnected && status.textContent === "Saved") {
            status.textContent = "";
          }
        }, 1800);
      }
    }

    async changeLine(lineKey, quantity, focusTarget) {
      if (!lineKey) return;

      this.clearError();
      this.setBusy(true);

      try {
        await this.saveNote({ silent: true }).catch(() => {});

        const data = await this.post(this.changeUrl, {
          id: lineKey,
          quantity,
          sections: [this.sectionId],
          sections_url: this.sectionsUrl,
        });

        const nextRoot = this.replaceSection(data);
        if (!nextRoot) {
          window.location.reload();
          return;
        }

        const count = Number.parseInt(nextRoot.dataset.cartCount, 10) || 0;
        setHeaderCartCount(count);
        dispatchCartEvents({ cart: data, itemCount: count });
        this.restoreFocus(nextRoot, focusTarget);
      } catch (error) {
        this.showError(error.message);
        this.setBusy(false);
      }
    }

    async addRecommendation(button) {
      const variantId = Number.parseInt(button.dataset.variantId, 10);
      if (!variantId) return;

      const productTitle = button.dataset.productTitle || "Product";
      const addedLabel = button.dataset.addedLabel || "Added";

      this.clearError();
      this.setBusy(true);

      try {
        await this.saveNote({ silent: true }).catch(() => {});

        const data = await this.post(this.addUrl, {
          items: [{ id: variantId, quantity: 1 }],
          sections: [this.sectionId],
          sections_url: this.sectionsUrl,
        });

        const nextRoot = this.replaceSection(data);
        if (!nextRoot) {
          window.location.reload();
          return;
        }

        const count = Number.parseInt(nextRoot.dataset.cartCount, 10) || 0;
        setHeaderCartCount(count);
        dispatchCartEvents({ cart: data, itemCount: count });
        showToast(nextRoot, `${productTitle} ${addedLabel.toLowerCase()} to your cart.`);
      } catch (error) {
        this.showError(error.message);
        this.setBusy(false);
      }
    }

    replaceSection(data) {
      const sectionHtml = data?.sections?.[this.sectionId];
      if (typeof sectionHtml !== "string" || sectionHtml.length === 0) return null;

      const parsed = new DOMParser().parseFromString(sectionHtml, "text/html");
      const candidate = parsed.querySelector(ROOT_SELECTOR);
      if (!candidate) return null;

      const nextRoot = document.importNode(candidate, true);
      this.root.replaceWith(nextRoot);
      mountRoot(nextRoot);
      return nextRoot;
    }

    restoreFocus(root, focusTarget) {
      if (!focusTarget?.lineKey) return;

      const line = [...root.querySelectorAll("[data-dusha-line]")].find(
        (element) => element.dataset.lineKey === focusTarget.lineKey,
      );
      if (!line) return;

      const target = focusTarget.action
        ? line.querySelector(
            `[data-dusha-quantity-button][data-action="${focusTarget.action}"]`,
          )
        : line.querySelector("[data-dusha-quantity-input]");

      target?.focus({ preventScroll: true });
    }
  }

  function mountRoot(root) {
    if (!(root instanceof Element)) return;
    const cart = new DushaCart(root);
    cart.mount();
  }

  function mountAll(container = document) {
    if (container.matches?.(ROOT_SELECTOR)) mountRoot(container);
    container.querySelectorAll?.(ROOT_SELECTOR).forEach(mountRoot);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => mountAll());
  } else {
    mountAll();
  }

  document.addEventListener("shopify:section:load", (event) => {
    mountAll(event.target);
  });
})();
